import { useMutation, useQuery } from '@tanstack/react-query';
import { GamifyToast } from 'application/common/CustomToasts';
import {
  leadImportService,
  LeadImportApiError,
} from '../services/leadImportService';
import { POLLING_INTERVAL_MS } from '../utils/constants';
import type {
  UploadUrlRequest,
  UploadUrlResponse,
  FileAnalysisResponse,
  SheetSelectionResponse,
  FileInfo,
  UniqueValuesResponse,
  ValidationResponse,
  MappingConfig,
  CreateJobRequest,
  CreateJobResponse,
  JobStatusResponse,
  SavedConfig,
} from '../utils/types';

/**
 * Centralized error handler for React Query onError callbacks.
 * Displays appropriate toast notifications based on error type and HTTP status.
 */
export function handleApiError(error: unknown): void {
  if (error instanceof LeadImportApiError) {
    switch (error.status) {
      case 400:
        // Handled inline by the component via mutation error state
        break;
      case 403:
        GamifyToast.error(
          'Access denied',
          'Please contact your administrator.'
        );
        break;
      case 404:
        // Handled by component — show not-found state
        break;
      case 500:
      default:
        GamifyToast.error('Something went wrong', 'Please try again.');
    }
  } else {
    GamifyToast.error(
      'Network error',
      'Please check your connection and try again.'
    );
  }
}

/**
 * Mutation hook to request a presigned upload URL from the Lead Import Service.
 */
export function useUploadUrl() {
  return useMutation<UploadUrlResponse, Error, UploadUrlRequest>({
    mutationFn: (req) => leadImportService.getUploadUrl(req),
    onError: handleApiError,
  });
}

/**
 * Mutation hook to trigger file analysis after upload.
 */
export function useAnalyzeFile() {
  return useMutation<FileAnalysisResponse, Error, string>({
    mutationFn: (fileID) => leadImportService.analyzeFile(fileID),
    onError: handleApiError,
  });
}

/**
 * Mutation hook to select a sheet from a multi-sheet XLSX file.
 */
export function useSelectSheet() {
  return useMutation<
    SheetSelectionResponse,
    Error,
    { fileID: string; sheetName: string }
  >({
    mutationFn: ({ fileID, sheetName }) =>
      leadImportService.selectSheet(fileID, sheetName),
    onError: handleApiError,
  });
}

/**
 * Query hook to list previously uploaded files.
 */
export function useListFiles() {
  return useQuery<FileInfo[], Error>({
    queryKey: ['lead-import', 'files'],
    queryFn: () => leadImportService.listFiles(),
    staleTime: 0,
    refetchOnMount: 'always',
  });
}

/**
 * Query hook to get a single file's info by ID.
 * Disabled when fileID is null.
 */
export function useGetFile(fileID: string | null) {
  return useQuery<FileInfo, Error>({
    queryKey: ['lead-import', 'file', fileID],
    queryFn: () => leadImportService.getFile(fileID!),
    enabled: !!fileID,
  });
}

/**
 * Mutation hook to fetch unique values for a column in a file.
 */
export function useUniqueValues() {
  return useMutation<
    UniqueValuesResponse,
    Error,
    { fileID: string; columnName: string }
  >({
    mutationFn: ({ fileID, columnName }) =>
      leadImportService.getUniqueValues(fileID, columnName),
    onError: handleApiError,
  });
}

/**
 * Mutation hook to validate a mapping configuration against a file.
 */
export function useValidateMapping() {
  return useMutation<
    ValidationResponse,
    Error,
    { fileID: string; config: MappingConfig }
  >({
    mutationFn: ({ fileID, config }) =>
      leadImportService.validateMapping(fileID, config),
    onError: handleApiError,
  });
}

/**
 * Mutation hook to create an import job.
 */
export function useCreateJob() {
  return useMutation<CreateJobResponse, Error, CreateJobRequest>({
    mutationFn: (req) => leadImportService.createJob(req),
    onError: handleApiError,
  });
}

/**
 * Query hook to poll job status at a fixed interval.
 * Uses POLLING_INTERVAL_MS from constants for refetchInterval when enabled.
 * Disabled when jobID is null or enabled is false.
 */
export function useJobStatus(jobID: string | null, enabled: boolean) {
  return useQuery<JobStatusResponse, Error>({
    queryKey: ['lead-import', 'job', jobID],
    queryFn: () => leadImportService.getJobStatus(jobID!),
    enabled: !!jobID && enabled,
    refetchInterval: enabled ? POLLING_INTERVAL_MS : false,
  });
}

/**
 * Query hook to list saved mapping configurations.
 */
export function useListConfigs() {
  return useQuery<SavedConfig[], Error>({
    queryKey: ['lead-import', 'configs'],
    queryFn: () => leadImportService.listConfigs(),
  });
}

/**
 * Mutation hook to save a mapping configuration for future reuse.
 */
export function useSaveConfig() {
  return useMutation<
    SavedConfig,
    Error,
    { name: string; config: MappingConfig }
  >({
    mutationFn: ({ name, config }) =>
      leadImportService.saveConfig(name, config),
    onError: handleApiError,
  });
}
